import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useStore';
import { authAPI } from '../services/api';
import { paginaIniziale } from '../navigazione/paginaIniziale';
import '../styles/CambiaPassword.css';

const LUNGHEZZA_MINIMA = 8;

/* Icone inline (SVG), nessuna dipendenza esterna */
const IcoOcchio = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const IcoOcchioBarrato = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.6 6.1A9.9 9.9 0 0 1 12 5.5c6.4 0 10 6.5 10 6.5a17.6 17.6 0 0 1-3.3 4.1M6.4 7.7A17.4 17.4 0 0 0 2 12s3.6 6.5 10 6.5a9.7 9.7 0 0 0 4-.8" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
        <path d="M3 3l18 18" />
    </svg>
);

/** Campo password con pulsante mostra/nascondi. */
const CampoPassword = ({ id, label, valore, onChange, placeholder, autoComplete, disabled }) => {
    const [visibile, setVisibile] = useState(false);
    return (
        <div className="cp-group">
            <label htmlFor={id}>{label}</label>
            <div className="cp-input-wrap">
                <input
                    type={visibile ? 'text' : 'password'}
                    id={id}
                    value={valore}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    required
                    disabled={disabled}
                />
                <button
                    type="button"
                    className="cp-occhio"
                    onClick={() => setVisibile((v) => !v)}
                    disabled={disabled}
                    aria-label={visibile ? 'Nascondi la password' : 'Mostra la password'}
                    title={visibile ? 'Nascondi la password' : 'Mostra la password'}
                >
                    {visibile ? <IcoOcchioBarrato /> : <IcoOcchio />}
                </button>
            </div>
        </div>
    );
};

const CambiaPassword = () => {
    const [attuale, setAttuale] = useState('');
    const [nuova, setNuova] = useState('');
    const [conferma, setConferma] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);
    const aggiornaUser = useAuthStore((state) => state.aggiornaUser);
    const logout = useAuthStore((state) => state.logout);

    // Riscontro immediato mentre si digita, senza aspettare l'invio.
    const troppoCorta = nuova.length > 0 && nuova.length < LUNGHEZZA_MINIMA;
    const coincidono = conferma.length > 0 && nuova === conferma;
    const divergono = conferma.length > 0 && nuova !== conferma;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (nuova.length < LUNGHEZZA_MINIMA) {
            setError(`La nuova password deve avere almeno ${LUNGHEZZA_MINIMA} caratteri.`);
            return;
        }
        if (nuova !== conferma) {
            setError('Le due password non coincidono.');
            return;
        }
        if (nuova === attuale) {
            setError('La nuova password deve essere diversa da quella attuale.');
            return;
        }

        setLoading(true);
        try {
            const response = await authAPI.cambiaPassword({
                password_attuale: attuale,
                password_nuova: nuova,
            });
            if (response.data.success) {
                aggiornaUser({ deve_cambiare_password: false });
                navigate(paginaIniziale(useAuthStore.getState().user), { replace: true });
            }
        } catch (err) {
            setError(err.message || 'Non è stato possibile cambiare la password.');
        } finally {
            setLoading(false);
        }
    };

    const handleEsci = () => {
        logout();
        navigate('/login', { replace: true });
    };

    return (
        <div className="cp-container">
            <div className="cp-card">
                <div className="cp-header">
                    <div className="cp-logo">R</div>
                    <h1 className="cp-titolo">Imposta una nuova password</h1>
                    <p className="cp-sottotitolo">
                        {user?.nome ? `${user.nome}, la ` : 'La '}
                        password che hai ricevuto è provvisoria. Scegline una tua per continuare.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="cp-form">
                    {error && <div className="cp-errore">{error}</div>}

                    <CampoPassword
                        id="attuale"
                        label="Password attuale"
                        valore={attuale}
                        onChange={setAttuale}
                        placeholder="Quella ricevuta"
                        autoComplete="current-password"
                        disabled={loading}
                    />

                    <div className="cp-blocco">
                        <CampoPassword
                            id="nuova"
                            label="Nuova password"
                            valore={nuova}
                            onChange={setNuova}
                            placeholder={`Almeno ${LUNGHEZZA_MINIMA} caratteri`}
                            autoComplete="new-password"
                            disabled={loading}
                        />
                        {troppoCorta && (
                            <div className="cp-riscontro cp-riscontro-ko">
                                Mancano {LUNGHEZZA_MINIMA - nuova.length} caratteri
                            </div>
                        )}
                    </div>

                    <div className="cp-blocco">
                        <CampoPassword
                            id="conferma"
                            label="Ripeti la nuova password"
                            valore={conferma}
                            onChange={setConferma}
                            placeholder="Riscrivila per conferma"
                            autoComplete="new-password"
                            disabled={loading}
                        />
                        {coincidono && (
                            <div className="cp-riscontro cp-riscontro-ok">✓ Le password coincidono</div>
                        )}
                        {divergono && (
                            <div className="cp-riscontro cp-riscontro-ko">Le password non coincidono</div>
                        )}
                    </div>

                    <button type="submit" className="cp-btn" disabled={loading}>
                        {loading ? 'Salvataggio…' : 'Salva e continua'}
                    </button>
                </form>

                <div className="cp-piede">
                    <span>Accesso come <strong>{user?.username || '—'}</strong></span>
                    <button type="button" className="cp-esci" onClick={handleEsci} disabled={loading}>
                        Esci
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CambiaPassword;
